import {NgModule, inject} from '@angular/core';
import {ActivatedRouteSnapshot, RouterModule, Routes, RouterStateSnapshot} from '@angular/router';
import {DashboardComponent} from "./components/dashboard/dashboard.component";
import {RegisterComponent} from "./register/register.component";
import {ReservedAreaComponent} from "./components/reserved-area/reserved-area.component";
import {RegisterStaffComponent} from './register/register-staff/register-staff.component';
import {AuthGuard} from "./secure";
import {CompaniesListComponent} from "./components/companies-list/company-list.component";
import {AgentsListComponent} from "./components/agents-list/agents-list.component";
import {EditProfileComponent} from "./components/edit-profile/edit-profile.component";
import {AthleteDocumentsComponent} from "./components/athlete-documents/athlete-documents.component";
import {UserSubscriptionsComponent} from "./components/user-subscriptions/user-subscriptions.component";
import {EditUserComponent} from "./components/edit-user/edit-user.component";
import {InvoicesListComponent} from "./components/invoices-list/invoices-list.component";
import {CategoriesListComponent} from "./components/categories-list/categories-list.component";
import {CompaniesSubscriptionsComponent} from "./components/companies-subscriptions/companies-subscriptions.component";
import {SubscriptionsCategoryComponent} from "./components/subscriptions-category/subscriptions-category.component";
import {CompaniesSettingsComponent} from "./components/companies-settings/companies-settings.component";
import {AgentDetailsComponent} from "./components/agent/agent-details/agent-details.component";
import {PaymentComponent} from "./components/payment/payment.component";
import {ThankyouComponent} from "./components/thankyou/thankyou.component";
import {ErrorPageComponent} from "./components/error-page/error-page.component";
import {IbanPageComponent} from "./components/iban-page/iban-page.component";
import {MessagesComponent} from "./components/messages/messages.component";
import {ResetPasswordComponent} from "./components/reset-password/reset-password.component";
import {CompanyBoardingComponent} from "./components/company-boarding/company-boarding.component";
import {ForgotPasswordComponent} from "./components/forgot-password/forgot-password.component";
import {ResetPasswordEmailComponent} from "./components/reset-password-email/reset-password-email.component";
import {AdminsSettingsComponent} from "./components/admins-settings/admins-settings.component";
import {AdminSubscriptionsComponent} from "./components/admin-subscriptions/admin-subscriptions.component";
import {MembershipComponent} from "./components/membership/membership.component";
import {AdminAllusersComponent} from "./components/admin-allusers/admin-allusers.component";
import {AdminUserDetailComponent} from "./components/admin-allusers/user-detail/admin-user-detail.component";
import {CompaniesAllusersComponent} from "./components/companies-allusers/companies-allusers.component";
import {OtpPageComponent} from "./components/otp-page/otp-page.component";
import {CompaniesShopComponent} from "./components/companies-shop/companies-shop.component";
import {SizeComponent} from "./components/companies-shop/size/size.component";
import {ColorComponent} from "./components/companies-shop/color/color.component";
import {ShopCategoryComponent} from './components/companies-shop/category/shop-category.component';
import {ShopComponent} from "./components/shop/shop.component";
import {EditProductComponent} from "./components/companies-shop/edit-product/edit-product.component";
import {EditBarcodeComponent} from './components/companies-shop/barcode/edit-barcode/edit-barcode.component';
import {
    BarcodeMovementEditComponent
} from "./components/companies-shop/barcode/barcode-movement/barcode-movement-edit/barcode-movement-edit.component";
import {CartComponent} from './components/shop/cart/cart.component';
import {CheckoutComponent} from "./components/shop/checkout/checkout.component";
import {OrdersComponent} from "./components/companies-shop/orders/orders.component";
import {
    CompanySubscriptionRequiredComponent
} from "./components/company-subscription-required/company-subscription-required.component";
import {CompanyPaymentIssueComponent} from "./components/company-payment-issue/company-payment-issue.component";
import {AdminAllAthletesComponent} from "./components/admin-allathletes/admin-allathletes.component";
import {
    DashboardMisterConvocationDetailComponent
} from "./components/dashboard/dashboard-mister/mister-convocation-detail/mister-convocation-detail.component";
import {
    AthleteConvocationsComponent
} from "./components/dashboard/dashboard-athlete/athlete-convocations/athlete-convocations.component";
import {AthleteNewComponent} from "./components/athlete-new/athlete-new.component";
import {RecurringPaymentDetailsComponent} from "./components/recurring-payment-details/recurring-payment-details.component";
import {environment} from "./environments/environment";

const routes: Routes = [
    ...(!environment.production ? [{
        path: 'demo/partial-payment',
        component: RecurringPaymentDetailsComponent,
        data: {customLayout: false, demo: true},
    }] : []),
    {
        path: '',
        redirectTo: '/login',
        pathMatch: "full",
        data: {
            title: 'Home'
        }
    },
    {
        path: 'login',
        loadChildren: () => import('./login/login.module').then(m => m.LoginModule),
        data: {
            customLayout: false
        }
    },
    {
        path: 'confirm-otp',
        component: OtpPageComponent,
    },
    {
        path: 'register',
        component: RegisterComponent,
        data: {
            customLayout: false
        }
    },
    {
        path: 'register/companies',
        component: RegisterComponent,
        data: {
            customLayout: false,
            typeOfRegistration: 'company'
        }
    },
    {
        path: 'register/athletes',
        component: RegisterComponent,
        data: {
            customLayout: false,
            typeOfRegistration: 'athlete'
        }
    },
    {
        path: 'register/agents',
        component: RegisterComponent,
        data: {
            customLayout: false,
            typeOfRegistration: 'agent'
        }
    },
    {
        path: 'register/staff',
        component: RegisterComponent,
        data: {
            customLayout: false,
            typeOfRegistration: 'staff'
        }
    },
    { path: 'reserved-area', component: ReservedAreaComponent },
    {path: 'shop', redirectTo: 'shop/products', pathMatch: "full",},
    {
        path: 'shop',
        data: {
            customLayout: true,
            shopLayout: true
        },
        children: [
            {
                path: 'products',
                component: ShopComponent,
            },
            {
                path: 'cart',
                component: CartComponent,
            },
            {
                path: 'checkout',
                component: CheckoutComponent,
            }
        ]
    },
    {path: 'admins', redirectTo: 'admins/dashboard', pathMatch: "full"},
    {
        path: 'admins',
        canActivate: [AuthGuard],
        data: {
            customLayout: true
        },
        children: [
            {
                path: 'dashboard',
                component: DashboardComponent,
            },
            {
                path: 'payment-installments/:id',
                component: RecurringPaymentDetailsComponent,
            },
            {
                path: 'companies',
                component: CompaniesListComponent,
                data: {
                    alternativeTableLayout: true
                }
            },
            {
                path: 'domains',
                component: AdminSubscriptionsComponent,
                data: {
                    fullBleed: true
                }
            },
            {
                path: 'invoices',
                component: InvoicesListComponent
            },
            {
                path: 'messages',
                component: MessagesComponent
            },
            {
                path: 'settings',
                component: AdminsSettingsComponent
            },
            {
                path: 'agents',
                component: AgentsListComponent,
            },
            {
                path: 'agents/:agentId',
                component: AgentDetailsComponent,
            },
            {
                path: 'settings',
                component: AgentsListComponent,
            },
            {
                path: 'categories',
                component: AgentsListComponent,
            },
            {
                path: 'instructions',
                component: AgentsListComponent,
            },
            {
                path: 'shop',
                component: ShopComponent,
            },
            {
                path: 'allUsers',
                component: AdminAllusersComponent,
            },
            {
                path: 'allUsers/:userId',
                component: AdminUserDetailComponent,
            },
            {
                path: 'allAthletes',
                component: AdminAllAthletesComponent,
            },
            {
                path: 'orders',
                component: OrdersComponent
            },
            {
                path: 'subscriptions',
                component: AdminSubscriptionsComponent,
                data: {
                    fullBleed: true
                }
            },
        ]
    },
    {path: 'companies', redirectTo: 'companies/dashboard', pathMatch: "full"},
    {
        path: 'companies',
        canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
        data: {
            customLayout: true
        },
        children: [
            {
                path: 'payment-required',
                component: CompanySubscriptionRequiredComponent,
            },
            {
                path: 'payment-installments/:id',
                component: RecurringPaymentDetailsComponent,
            },
            {
                path: 'payment-issue',
                component: CompanyPaymentIssueComponent,
            },
            {
                path: 'dashboard',
                component: DashboardComponent,
                canActivate: [
                    AuthGuard,
                    (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state),
                    (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyDashboard(route, state),
                ],
            },
            {
                path: 'invoices',
                component: InvoicesListComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'categories',
                component: CategoriesListComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'subscriptions',
                component: CompaniesSubscriptionsComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'subscriptions/category/:categoryId',
                component: SubscriptionsCategoryComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'subscriptions/category/:categoryId/convocations/:id',
                component: DashboardMisterConvocationDetailComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'athlete/:athleteId',
                component: EditProfileComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'messages',
                component: MessagesComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'athletes/onboarding',
                component: RegisterComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
                data: {
                    typeOfRegistration: 'athlete',
                    manualOnboarding: true,
                    customLayout: false
                }
            },
            {
                path: 'athletes',
                component: CompaniesAllusersComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: ':companyId/settings',
                component: CompaniesSettingsComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'shop',
                component: CompaniesShopComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'shop/sizes',
                component: SizeComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'shop/edit',
                component: EditProductComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'shop/colors',
                component: ColorComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'shop/categories',
                component: ShopCategoryComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'shop/orders',

                component: OrdersComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'shop/barcode/edit',
                component: EditBarcodeComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            },
            {
                path: 'shop/barcodeMovement/edit',
                component: BarcodeMovementEditComponent,
                canActivate: [AuthGuard, (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => inject(AuthGuard).canAccessCompanyArea(route, state)],
            }

        ]
    },
    {
        path: 'companies/:companyId/boarding',
        component: CompanyBoardingComponent,
    },
    {path: 'users', redirectTo: 'users/dashboard', pathMatch: "full"},
    {
        path: 'users',
        canActivate: [AuthGuard],
        data: {customLayout: true},
        children: [
            {
                path: 'dashboard',
                component: DashboardComponent,
            },
            {
                path: 'athletes/new',
                component: AthleteNewComponent,
            },
            {
                path: 'profile/:athleteId',
                component: EditProfileComponent,
            },
            {
                path: 'documents/:athleteId',
                component: AthleteDocumentsComponent,
            },
            {
                path: 'subscriptions/:athleteId',
                component: UserSubscriptionsComponent,
            },
            {
                path: 'edit-parents/:athleteId',
                data: {editParent: true},
                component: EditUserComponent,
            },
            {
                path: 'edit-athlete/:athleteId',
                data: {editAthlete: true},
                component: EditUserComponent,
            },
            {
                path: 'payment/:athleteId',
                component: PaymentComponent
            },
            {
                path: 'messages',
                component: MessagesComponent
            },
            {
                path: 'messages/:messageId',
                component: MessagesComponent
            },
            {
                path: 'convocations',
                component: AthleteConvocationsComponent,
            },
            {
                path: 'convocations/:id',
                component: DashboardMisterConvocationDetailComponent,
                data: {
                    readOnly: true,
                    backRoute: ['/users/convocations'],
                },
            },
            {
                path: 'membership/:athleteId',
                component: MembershipComponent
            },
            {
                path: 'orders',
                component: OrdersComponent
            },

        ],
    },
    {path: 'agents', redirectTo: 'agents/dashboard', pathMatch: "full"},
    {
        path: 'agents',
        canActivate: [AuthGuard],
        data: {customLayout: true},
        children: [
            {
                path: 'dashboard',
                component: DashboardComponent,
            },
            {
                path: 'details/:agentId',
                component: AgentDetailsComponent,
            },
            {
                path: 'invoices',
                component: InvoicesListComponent,
            },
            {
                path: 'companies',
                component: CompaniesListComponent,
                data: {
                    alternativeTableLayout: true
                }
            },
        ],
    },
    {path: 'misters', redirectTo: 'misters/dashboard', pathMatch: 'full'},
    {
        path: 'misters',
        canActivate: [AuthGuard],
        data: {customLayout: true},
        children: [
            {
                path: 'dashboard',
                component: DashboardComponent,
            },
            {
                path: 'convocations/:id',
                component: DashboardMisterConvocationDetailComponent,
            },
        ],
    },
    {
        path: 'dashboard/mister/convocations/:id',
        canActivate: [AuthGuard],
        data: {
            customLayout: true,
        },
        component: DashboardMisterConvocationDetailComponent,
    },
    {
        path: 'thankyou',
        data: {customLayout: true},
        component: ThankyouComponent,
    },
    {
        path: 'error',
        data: {customLayout: true},
        canActivate: [AuthGuard],
        component: ErrorPageComponent
    },
    {
        path: 'iban-details',
        data: {customLayout: true},
        component: IbanPageComponent
    },
    {
        path: 'reset-password',
        data: {customLayout: true},
        canActivate: [AuthGuard],
        component: ResetPasswordComponent
    },
    {
        path: 'forgot-password',
        component: ForgotPasswordComponent
    },
    {
        path: 'reset-password/:email',
        component: ResetPasswordEmailComponent
    },
    {path: '**', data: {customLayout: true}, component: DashboardComponent},

];

@NgModule({
    imports: [RouterModule.forRoot(routes, {
        initialNavigation: 'enabledBlocking'
    })],
    exports: [RouterModule]
})
export class AppRoutingModule {
}
